import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CheckCircle, XCircle, Copy, Globe, Upload, Download, Settings, Loader2, AlertCircle,
  Info, Check, X, ArrowRight, ChevronDown, FileText, Table, BookText
} from 'lucide-react';
const XLSX = require("xlsx");
/**
 * Simplified `cn` utility function for merging Tailwind classes.
 * This function mimics the behavior of `clsx` and `tailwind-merge` for basic usage,
 * ensuring that classes are combined efficiently and duplicates/overrides are handled.
 * @param {...(string|string[]|object)} inputs - Class names or objects to merge.
 * @returns {string} The merged class string.
 */
const cn = (...inputs) => {
  const classes = [];
  for (const input of inputs) {
    if (typeof input === 'string') {
      classes.push(input);
    } else if (Array.isArray(input)) {
      classes.push(...input.filter(Boolean));
    } else if (typeof input === 'object' && input !== null) {
      for (const key in input) {
        if (input[key]) {
          classes.push(key);
        }
      }
    }
  }
  return Array.from(new Set(classes.join(' ').split(/\s+/).filter(Boolean))).join(' ');
};

/**
 * A custom Button component inspired by shadcn/ui and Aceternity's subtle gradients.
 * It provides different visual variants and sizes using Tailwind CSS.
 * @param {object} props - Component props.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {'default'|'destructive'|'outline'|'secondary'|'ghost'|'link'} [props.variant='default'] - Visual variant of the button.
 * @param {'default'|'sm'|'lg'|'icon'} [props.size='default'] - Size of the button.
 * @param {boolean} [props.asChild=false] - If true, renders the component as a child of the element passed to it.
 * @param {React.Ref} ref - Ref to forward to the underlying DOM element.
 * @returns {JSX.Element} The Button component.
 */
const Button = React.forwardRef(({ className, variant = 'default', size = 'default', asChild = false, ...props }, ref) => {
  const Comp = asChild ? 'span' : 'button';
  const baseClasses = "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    'default': "bg-gradient-to-br from-indigo-600 to-blue-700 text-white shadow-lg hover:from-indigo-700 hover:to-blue-800",
    'destructive': "bg-gradient-to-br from-red-600 to-rose-700 text-white shadow-sm hover:from-red-700 hover:to-rose-800",
    'outline': "border border-gray-700 bg-gray-900 text-gray-300 shadow-sm hover:bg-gray-800 hover:text-white",
    'secondary': "bg-gray-700 text-gray-300 shadow-sm hover:bg-gray-600",
    'ghost': "hover:bg-gray-700 hover:text-white",
    'link': "text-blue-400 underline-offset-4 hover:underline",
  };

  const sizeClasses = {
    'default': "h-10 px-4 py-2",
    'sm': "h-9 px-3 text-xs",
    'lg': "h-11 px-8",
    'icon': "h-9 w-9 p-0 flex items-center justify-center",
  };

  return (
    <Comp
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";


/**
 * Delays execution for a given number of milliseconds.
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calls the Google Translate API with retry logic and enhanced error handling.
 * @param {string} text - The text to translate.
 * @param {string} targetLang - The target language code.
 * @param {number} [retries=3] - The number of retry attempts in case of failure.
 * @returns {Promise<string>} The translated text.
 * @throws {Error} If translation fails after all retries.
 */
async function translateWithGoogle(text, targetLang, retries = 3) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`
      );
      if (response.status === 429) {
        await delay(Math.pow(2, attempt) * 1000);
        continue;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data && data[0] && data[0][0] && data[0][0][0]) {
        return data[0][0][0];
      } else {
        throw new Error("Unexpected translation API response format.");
      }
    } catch (error) {
      if (attempt === retries) {
        throw new Error(`Translation failed after ${retries} attempts: ${error.message}`);
      }
      await delay(1000 * attempt);
    }
  }
}

const localeToLibreTranslate = {
  'bn_BD': 'bn', 'cn_MY': 'zh', 'de_DE': 'de', 'en': 'en', 'es_ES': 'es',
  'fr_FR': 'fr', 'ht_HT': 'ht', 'id_ID': 'id', 'ja_JP': 'ja', 'km_KH': 'km',
  'ko_KR': 'ko', 'my_MM': 'my', 'ne_NP': 'ne', 'pt_BR': 'pt', 'ru_RU': 'ru',
  'sw_TZ': 'sw', 'th_TH': 'th', 'tr_TR': 'tr', 'vi_VN': 'vi', 'zh_CN': 'zh', 'zh_TW': 'zh'
};

const languageNames = {
  'en': 'English',
  'km_KH': 'Khmer'
};

const originalLanguages = {
  tycho: [
    'bn_BD', 'cn_MY', 'de_DE', 'en', 'es_ES', 'fr_FR', 'ht_HT', 'id_ID',
    'ja_JP', 'km_KH', 'ko_KR', 'my_MM', 'ne_NP', 'pt_BR', 'ru_RU',
    'sw_TZ', 'th_TH', 'tr_TR', 'vi_VN', 'zh_CN', 'zh_TW'
  ],
  kirby: [
    'cn_MY', 'en', 'es_ES', 'id_ID', 'ja_JP', 'km_KH', 'ko_KR',
    'pt_BR', 'ru_RU', 'th_TH', 'vi_VN', 'zh_CN', 'zh_TW'
  ]
};

const translations = {
  en: {
    appTitlePart1: "Multi-Language",
    appTitlePart2: "Key-Value Translator",
    poweredBy: "Powered by Rithy Tep",
    poweredBySubtitle: "Powered by Rithy Tep",
    translationSettings: "Translation Settings",
    translationKey: "Translation Key",
    sourceValue: "Source Value (English)",
    targetProject: "Target Project",
    translateSingle: "Translate Single",
    batchTranslate: "Batch Translate",
    consoleOutput: "Console Output",
    live: "Live",
    clearConsole: "Clear Console",
    viewLastResult: "View Last Result",
    translationDelay: "Translation Delay (ms)",
    compactMode: "Compact Mode",
    save: "Save",
    translationComplete: "Translation Complete!",
    resultsSummary: (total, success, errors) => `Translation process completed for ${total} languages.\n\nResults summary:\n${success} successful translations\n${errors} failed translations`,
    generatedJsonOutput: "Generated JSON Output",
    download: "Download",
    copyJson: "Copy JSON",
    close: "Close",
    batchTranslation: "Batch Translation",
    inputFormat: "Input Format",
    keyValuePairs: "Key: Value pairs (one per line)",
    jsonObject: "JSON object",
    cancel: "Cancel",
    startBatchTranslation: "Start Batch Translation",
    translatorReady: "Multi-Language Key-Value Translator Ready!",
    newFeatures: "New features: Batch translation, progress tracking, enhanced error handling",
    customizeSettings: "Click Settings to customize translation behavior",
    readyForTasks: "Ready for translation tasks!",
    startingBatch: (numPairs) => `Starting batch translation for ${numPairs} key-value pairs`,
    targetLanguages: (numLangs, totalOps) => `Target: ${numLangs} languages | Total operations: ${totalOps}`,
    processing: (key, value) => `Processing: "${key}" = "${value}"`,
    translatedValue: (lang, value) => `${lang}: "${value.substring(0, 50)}${value.length > 50 ? '...' : ''}"`,
    translationFailed: (lang, error) => `${lang}: Translation failed - ${error}`,
    batchCompleted: "Batch translation completed!",
    finalStats: (success, errors) => `Final stats: ${success} successful, ${errors} failed`,
    errorEnterKeyValue: "Error: Please enter both a key and a value",
    jsonCopied: "JSON copied to clipboard!",
    noJsonToCopy: "No JSON output to copy",
    invalidJsonFormat: "Invalid JSON format",
    language: "Language",
    translating: "Translating",
    excelToJson: "Excel to JSON",
    jsonToExcel: "JSON to Excel",
    excelConverter: "Excel Converter",
    uploadExcelFile: "Upload Excel File",
    selectFile: "Select a file",
    dropFileHere: "Drop Excel file here or click to select",
    excelFileTypeError: "Invalid file type. Please upload an Excel (.xlsx or .xls) file.",
    parsedOutput: "Parsed Output",
    downloadOutput: "Download File",
    copyOutput: "Copy Output",
    enterJsonData: "Enter JSON Data",
    convertAndDownload: "Convert & Download Excel",
    invalidJsonInput: "Invalid JSON input.",
    downloadExcel: "Download Excel",
    excelParsingError: "Error parsing Excel file. This might be due to a corrupted file, an unsupported Excel format (e.g., very old/new versions), or the file being password-protected. Please ensure the file is not empty and has accessible data.",
    outputFormat: "Output Format",
    outputAsJson: "JSON",
    outputAsCsv: "CSV",
    downloadCsv: "Download CSV",
    textToCsv: "Text to CSV",
    enterKeyValueText: "Enter Key-Value Text",
    convertAndDownloadCsv: "Convert & Download CSV",
    csvCopied: "CSV copied to clipboard!",
    outputJsonThenCsv: "JSON then CSV"
  },
  km_KH: {
    appTitlePart1: "កម្មវិធីបកប្រែពាក្យច្រើនភាសា",
    appTitlePart2: "សម្រាប់តម្លៃគន្លឹះ",
    poweredBy: "ផ្ដល់ជូនដោយ រិទ្ធី ទឹប",
    poweredBySubtitle: "ដំណើរការដោយ Rithy Tep",
    translationSettings: "ការកំណត់ការបកប្រែ",
    translationKey: "គន្លឹះបកប្រែ",
    sourceValue: "តម្លៃប្រភព (អង់គ្លេស)",
    targetProject: "គម្រោងគោលដៅ",
    translateSingle: "បកប្រែមួយ",
    batchTranslate: "បកប្រែជាបាច់",
    consoleOutput: "លទ្ធផលកុងសូល",
    live: "ផ្ទាល់",
    clearConsole: "សម្អាតកុងសូល",
    viewLastResult: "មើលលទ្ធផលចុងក្រោយ",
    translationDelay: "ការពន្យារពេលបកប្រែ (មីលីវិនាទី)",
    compactMode: "របៀបបង្រួម",
    save: "រក្សាទុក",
    translationComplete: "ការបកប្រែបានបញ្ចប់!",
    resultsSummary: (total, success, errors) => `ដំណើរការបកប្រែបានបញ្ចប់សម្រាប់ ${total} ភាសា។\n\nសង្ខេបលទ្ធផល:\n${success} ការបកប្រែជោគជ័យ\n${errors} ការបកប្រែបរាជ័យ`,
    generatedJsonOutput: "លទ្ធផល JSON ដែលបានបង្កើត",
    download: "ទាញយក",
    copyJson: "ចម្លង JSON",
    close: "បិទ",
    batchTranslation: "បកប្រែជាបាច់",
    inputFormat: "ទម្រង់បញ្ចូល",
    keyValuePairs: "គន្លឹះ៖ គូរតម្លៃ (មួយជួរ)",
    jsonObject: "វត្ថុ JSON",
    cancel: "បោះបង់",
    startBatchTranslation: "ចាប់ផ្តើមបកប្រែជាបាច់",
    translatorReady: "កម្មវិធីបកប្រែពាក្យច្រើនភាសាបានត្រៀមរួចរាល់!",
    newFeatures: "លក្ខណៈពិសេសថ្មីៗ៖ ការបកប្រែជាបាច់ ការតាមដានដំណើរការ ការដោះស្រាយកំហុសប្រសើរឡើង",
    customizeSettings: "ចុចការកំណត់ដើម្បីប្ដូរឥរិយាបថបកប្រែ",
    readyForTasks: "ត្រៀមសម្រាប់កិច្ចការបកប្រែ!",
    startingBatch: (numPairs) => `កំពុងចាប់ផ្តើមបកប្រែជាបាច់សម្រាប់ ${numPairs} គូរតម្លៃគន្លឹះ`,
    targetLanguages: (numLangs, totalOps) => `គោលដៅ៖ ${numLangs} ភាសា | ប្រតិបត្តិការសរុប៖ ${totalOps}`,
    processing: (key, value) => `កំពុងដំណើរការ៖ "${key}" = "${value}"`,
    translatedValue: (lang, value) => `${lang}៖ "${value.substring(0, 50)}${value.length > 50 ? '...' : ''}"`,
    translationFailed: (lang, error) => `${lang}៖ ការបកប្រែបរាជ័យ - ${error}`,
    batchCompleted: "ការបកប្រែជាបាច់បានបញ្ចប់!",
    finalStats: (success, errors) => `ស្ថិតិចុងក្រោយ៖ ${success} ជោគជ័យ, ${errors} បរាជ័យ`,
    errorEnterKeyValue: "កំហុស៖ សូមបញ្ចូលទាំងគន្លឹះ និងតម្លៃ",
    jsonCopied: "ចម្លង JSON ទៅក្តារតម្បៀតខ្ទាស់!",
    noJsonToCopy: "គ្មានលទ្ធផល JSON សម្រាប់ចម្លង",
    invalidJsonFormat: "ទម្រង់ JSON មិនត្រឹមត្រូវ",
    language: "ភាសា",
    translating: "កំពុងបកប្រែ",
    excelToJson: "Excel ទៅ JSON",
    jsonToExcel: "JSON ទៅ Excel",
    excelConverter: "ឧបករណ៍បម្លែង Excel",
    uploadExcelFile: "ផ្ទុកឯកសារ Excel ឡើង",
    selectFile: "ជ្រើសរើសឯកសារ",
    dropFileHere: "ទម្លាក់ឯកសារ Excel នៅទីនេះ ឬចុចដើម្បីជ្រើសរើស",
    excelFileTypeError: "ប្រភេទឯកសារមិនត្រឹមត្រូវ។ សូមផ្ទុកឯកសារ Excel (.xlsx ឬ .xls) ឡើង។",
    parsedOutput: "លទ្ធផលដែលបានវិភាគ",
    downloadOutput: "ទាញយកឯកសារ",
    copyOutput: "ចម្លងលទ្ធផល",
    enterJsonData: "បញ្ចូលទិន្នន័យ JSON",
    convertAndDownload: "បម្លែង & ទាញយក Excel",
    invalidJsonInput: "ការបញ្ចូល JSON មិនត្រឹមត្រូវ។",
    downloadExcel: "ទាញយក Excel",
    excelParsingError: "កំហុសក្នុងការវិភាគឯកសារ Excel។ នេះអាចបណ្ដាលមកពីឯកសារខូច ទម្រង់ Excel មិនគាំទ្រ (ឧទាហរណ៍ កំណែចាស់/ថ្មីខ្លាំង) ឬឯកសារត្រូវបានការពារដោយពាក្យសម្ងាត់។ សូមប្រាកដថាឯកសារមិនទទេ ហើយមានទិន្នន័យដែលអាចចូលប្រើបាន។",
    outputFormat: "ទម្រង់លទ្ធផល",
    outputAsJson: "JSON",
    outputAsCsv: "CSV",
    downloadCsv: "ទាញយក CSV",
    textToCsv: "អត្ថបទទៅ CSV",
    enterKeyValueText: "បញ្ចូលអត្ថបទគន្លឹះ-តម្លៃ",
    convertAndDownloadCsv: "បម្លែង និងទាញយក CSV",
    csvCopied: "ចម្លង CSV!",
    outputJsonThenCsv: "JSON រួច CSV"
  }
};


/**
 * Enhanced Progress Bar Component to display translation progress.
 * @param {object} props - Component props.
 * @param {number} props.current - Current number of completed operations.
 * @param {number} props.total - Total number of operations.
 * @param {number} props.successCount - Number of successful translations.
 * @param {number} props.errorCount - Number of failed translations.
 */
const ProgressBar = ({ current, total, successCount, errorCount }) => {
  const percentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className="w-full bg-gray-700 rounded-full h-3 mb-4 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-300 ease-out flex items-center justify-end pr-2"
        style={{ width: `${percentage}%` }}
      >
        {percentage > 20 && (
          <span className="text-xs text-white font-bold">
            {Math.round(percentage)}%
          </span>
        )}
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-2">
        <span>{current}/{total} completed</span>
        <span className="flex gap-4">
          <span className="text-emerald-400">✓ {successCount}</span>
          <span className="text-red-400">✗ {errorCount}</span>
        </span>
      </div>
    </div>
  );
};

/**
 * Enhanced Terminal Output Component for displaying real-time logs.
 * @param {object} props - Component props.
 * @param {Array<object>} props.messages - Array of log messages.
 * @param {boolean} props.isCompact - Whether to display in compact mode.
 */
const TerminalOutput = ({ messages, isCompact }) => {
  const outputRef = useRef(null);

  useEffect(() => {
    if (outputRef.current) {
      setTimeout(() => {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }, 50);
    }
  }, [messages]);

  /**
   * Returns a Lucide icon component based on message type.
   * @param {string} type - Type of the message (success, error, warning, info, prompt).
   * @returns {JSX.Element} Lucide icon component.
   */
  const getMessageIcon = (type) => {
    switch (type) {
      case 'success': return <Check className="w-4 h-4 text-emerald-400" />;
      case 'error': return <X className="w-4 h-4 text-red-400" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'info': return <Info className="w-4 h-4 text-blue-400" />;
      case 'prompt': return <ArrowRight className="w-4 h-4 text-lime-400" />;
      default: return null;
    }
  };

  return (
    <div
      ref={outputRef}
      className={cn(
        "terminal-output-area bg-gray-900 border border-gray-700 rounded-lg p-4",
        isCompact ? 'min-h-[200px] max-h-[400px]' : 'min-h-[330px] max-h-[330px]',
        "flex flex-col font-mono text-sm overflow-y-auto whitespace-pre-wrap"
      )}
    >
      <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
        <h3 className="text-emerald-400 font-bold text-base">Console Output</h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
          Live
        </div>
      </div>

      <div className="flex-grow space-y-1">
        {messages.map((msg, index) => (
          <div
            key={msg.id || index}
            className={cn(
              "flex items-start gap-2",
              msg.type === 'error' && 'text-red-400',
              msg.type === 'success' && 'text-emerald-400',
              msg.type === 'warning' && 'text-yellow-400',
              msg.type === 'info' && 'text-blue-400',
              msg.type === 'prompt' && 'text-lime-400 font-semibold',
              !['error', 'success', 'warning', 'info', 'prompt'].includes(msg.type) && 'text-gray-300'
            )}
          >
            <span className="flex-shrink-0 w-4 flex items-center justify-center">
              {getMessageIcon(msg.type)}
            </span>
            <span className="flex-grow break-words">{msg.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Enhanced Settings Panel for configuring translation options.
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Whether the panel is open.
 * @param {function} props.onClose - Callback to close the panel.
 * @param {object} props.settings - Current settings object.
 * @param {function} props.onSettingsChange - Callback to update settings.
 * @param {object} props.t - Translation function.
 */
const SettingsPanel = ({ isOpen, onClose, settings, onSettingsChange, t }) => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimateIn(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  if (!isOpen && !animateIn) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div className={cn(
        "bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md transition-all duration-300 ease-out",
        animateIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}>
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {t('translationSettings')}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XCircle className="w-6 h-6" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('translationDelay')}
            </label>
            <input
              type="number"
              min="100"
              max="5000"
              value={settings.translationDelay}
              onChange={(e) => onSettingsChange({ ...settings, translationDelay: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Delay between API calls to avoid rate limiting</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="compactMode"
              checked={settings.compactMode}
              onChange={(e) => onSettingsChange({ ...settings, compactMode: e.target.checked })}
              className="rounded h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-900"
            />
            <label htmlFor="compactMode" className="text-sm text-gray-300">
              {t('compactMode')}
            </label>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t border-gray-700">
          <Button onClick={onClose}>
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Enhanced Result Modal to display translation results and options to copy/download.
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {function} props.onClose - Callback to close the modal.
 * @param {string} props.title - Title of the modal.
 * @param {string} props.message - Descriptive message for the modal.
 * @param {string} props.jsonOutput - The generated JSON string.
 * @param {function} props.onCopy - Callback when JSON is copied.
 * @param {object} props.stats - Translation statistics (success, errors, total).
 * @param {object} props.t - Translation function.
 */
const ResultModal = ({ isOpen, onClose, title, message, jsonOutput, onCopy, stats, t }) => {
  const [animateIn, setAnimateIn] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimateIn(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  if (!isOpen && !animateIn) return null;

  /** Handles copying the JSON output to the clipboard. */
  const handleCopy = async () => {
    if (!jsonOutput) return;
    try {
      await navigator.clipboard.writeText(jsonOutput);
      setCopySuccess(true);
      onCopy();
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = jsonOutput;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onCopy();
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  /** Handles downloading the JSON output as a file. */
  const downloadJson = () => {
    if (!jsonOutput) return;
    const blob = new Blob([jsonOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `translation_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div className={cn(
        "bg-gray-800 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden transition-all duration-300 ease-out",
        animateIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}>
        <div className="flex justify-end items-center p-4 border-b border-gray-700"> { }
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XCircle className="w-6 h-6" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <p className="text-gray-300 mb-4 whitespace-pre-wrap">{message}</p>

          {jsonOutput && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-200">{t('generatedJsonOutput')}</h4>
              <div className="bg-gray-900 p-4 rounded-md text-gray-200 font-mono text-sm overflow-auto max-h-96 border border-gray-700">
                <pre>{jsonOutput}</pre>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-6 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Generated at {new Date().toLocaleString()}
          </div>
          <div className="flex gap-3">
            {jsonOutput && (
              <>
              </>
            )}
            <Button
              onClick={onClose}
              variant="outline"
            >
              {t('close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Batch Input Component for uploading multiple key-value pairs for translation.
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Whether the batch input modal is open.
 * @param {function} props.onClose - Callback to close the modal.
 * @param {function} props.onSubmit - Callback to submit batch translation data.
 * @param {object} props.t - Translation function.
 */
const BatchInput = ({ isOpen, onClose, onSubmit, t }) => {
  const [batchText, setBatchText] = useState('');
  const [format, setFormat] = useState('keyvalue');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimateIn(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  if (!isOpen && !animateIn) return null;

  /** Handles the submission of batch text, parsing it based on selected format. */
  const handleSubmit = () => {
    const pairs = [];

    if (format === 'keyvalue') {
      const lines = batchText.split('\n').filter(line => line.trim());
      for (const line of lines) {
        const [key, ...valueParts] = line.split(':');
        if (key && valueParts.length > 0) {
          pairs.push({ key: key.trim(), value: valueParts.join(':').trim() });
        }
      }
    } else {
      try {
        const json = JSON.parse(batchText);
        for (const [key, value] of Object.entries(json)) {
          pairs.push({ key, value: String(value) });
        }
      } catch (error) {
        const errorMessage = `${t('invalidJsonFormat')}: ${error.message}`;
        console.error(errorMessage);
        return;
      }
    }

    onSubmit(pairs);
    onClose();
    setBatchText('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div className={cn(
        "bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl transition-all duration-300 ease-out",
        animateIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}>
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload className="w-5 h-5" />
            {t('batchTranslation')}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XCircle className="w-6 h-6" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('inputFormat')}
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="keyvalue">{t('keyValuePairs')}</option>
              <option value="json">{t('jsonObject')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {format === 'keyvalue' ? t('keyValuePairs') : t('jsonObject')}
            </label>
            <textarea
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-md text-gray-200 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
              placeholder={format === 'keyvalue'
                ? 'login_button: Login\nregister_text: Create Account\nwelcome_message: Welcome to our app'
                : '{\n  "login_button": "Login",\n  "register_text": "Create Account",\n  "welcome_message": "Welcome to our app"\n}'
              }
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
          <Button
            onClick={onClose}
            variant="outline"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!batchText.trim()}
          >
            {t('startBatchTranslation')}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * ExcelConverter component for converting Excel files to JSON or CSV.
 * Requires XLSX library.
 * @param {object} props - Component props.
 * @param {object} props.t - Translation function.
 * @param {boolean} props.isXLSXLoaded - Indicates if the XLSX library is loaded.
 */
const ExcelConverter = ({ t, isXLSXLoaded }) => {
  const [outputText, setOutputText] = useState('');
  const [fileName, setFileName] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [outputFormat, setOutputFormat] = useState('json');

  /**
   * Handles the file selection and conversion process based on selected output format.
   * @param {FileList} files - The list of selected files.
   */
  const handleFile = (files) => {
    const file = files[0];
    if (!file) return;

    setFileName(file.name);
    setError('');
    setOutputText('');

    if (!isXLSXLoaded || typeof XLSX === 'undefined') {
      setError("XLSX library is not available. Please wait or refresh the page.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        let convertedData;
        let finalOutputFormat = outputFormat;

        if (outputFormat === 'json') {
          convertedData = JSON.stringify(XLSX.utils.sheet_to_json(worksheet), null, 2);
        } else if (outputFormat === 'csv') {
          convertedData = XLSX.utils.sheet_to_csv(worksheet);
        } else if (outputFormat === 'json_then_csv') {
          const jsonFromExcel = XLSX.utils.sheet_to_json(worksheet);
          convertedData = XLSX.utils.json_to_csv(jsonFromExcel);
          finalOutputFormat = 'csv';
        }
        setOutputText(convertedData);
        fileInputRef.current.dataset.finalOutputFormat = finalOutputFormat;

      } catch (err) {
        console.error("Error parsing Excel file:", err);
        setError(t('excelParsingError'));
        setOutputText('');
      }
    };

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      reader.readAsArrayBuffer(file);
    } else {
      setError(t('excelFileTypeError'));
      setOutputText('');
    }
  };

  /** Handles copying the output text (JSON or CSV) to the clipboard. */
  const handleCopy = async () => {
    if (!outputText) return;
    try {
      await navigator.clipboard.writeText(outputText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = outputText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  /** Handles downloading the output text (JSON or CSV) as a file. */
  const downloadOutput = () => {
    if (!outputText) return;
    const currentOutputFormat = fileInputRef.current.dataset.finalOutputFormat || outputFormat;

    const fileExtension = currentOutputFormat === 'json' ? 'json' : 'csv';
    const mimeType = currentOutputFormat === 'json' ? 'application/json' : 'text/csv';
    const blob = new Blob([outputText], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName.split('.')[0] || 'converted'}.${fileExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (fileName && fileInputRef.current?.files[0]) {
      handleFile(fileInputRef.current.files);
    }
  }, [outputFormat, fileName]);


  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white flex items-center gap-2">
        <Table className="w-6 h-6" /> {t('excelConverter')}
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('outputFormat')}
        </label>
        <div className="flex gap-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="outputFormat"
              value="json"
              checked={outputFormat === 'json'}
              onChange={() => setOutputFormat('json')}
              className="rounded h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-900"
              disabled={!isXLSXLoaded}
            />
            <span className="ml-2 text-gray-300">{t('outputAsJson')}</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="outputFormat"
              value="csv"
              checked={outputFormat === 'csv'}
              onChange={() => setOutputFormat('csv')}
              className="rounded h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-900"
              disabled={!isXLSXLoaded}
            />
            <span className="ml-2 text-gray-300">{t('outputAsCsv')}</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="outputFormat"
              value="json_then_csv"
              checked={outputFormat === 'json_then_csv'}
              onChange={() => setOutputFormat('json_then_csv')}
              className="rounded h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-900"
              disabled={!isXLSXLoaded}
            />
            <span className="ml-2 text-gray-300">{t('outputJsonThenCsv')}</span>
          </label>
        </div>
      </div>

      <div
        className={cn(
          "border-2 border-dashed border-gray-600 rounded-lg p-6 text-center transition-colors",
          isXLSXLoaded ? "cursor-pointer hover:border-blue-500" : "cursor-not-allowed opacity-50"
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (isXLSXLoaded) handleFile(e.dataTransfer.files); }}
        onClick={() => { if (isXLSXLoaded) fileInputRef.current?.click(); }}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleFile(e.target.files)}
          accept=".xlsx, .xls"
          className="hidden"
          disabled={!isXLSXLoaded}
        />
        <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p className="text-gray-300">{t('dropFileHere')}</p>
        {fileName && <p className="text-blue-400 mt-2">File selected: {fileName}</p>}
        {error && <p className="text-red-400 mt-2">{error}</p>}
        {!isXLSXLoaded && <p className="text-yellow-400 mt-2">Loading Excel library...</p>}
      </div>

      {outputText && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-200">{t('parsedOutput')}</h3>
          <div className="bg-gray-900 p-4 rounded-md text-gray-200 font-mono text-sm overflow-auto max-h-96 border border-gray-700">
            <pre>{outputText}</pre>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={downloadOutput}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {fileInputRef.current.dataset.finalOutputFormat === 'json' ? t('downloadJson') : t('downloadCsv')}
            </Button>
            <Button
              onClick={handleCopy}
              className={cn("flex items-center gap-2", copySuccess ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white')}
            >
              <Copy className="w-4 h-4" />
              {copySuccess ? 'Copied!' : t('copyOutput')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * JsonToExcelConverter component for converting JSON data to an Excel file.
 * Requires XLSX library.
 * @param {object} props - Component props.
 * @param {object} props.t - Translation function.
 * @param {boolean} props.isXLSXLoaded - Indicates if the XLSX library is loaded.
 */
const JsonToExcelConverter = ({ t, isXLSXLoaded }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState('');

  /** Handles converting the JSON input to an Excel file and triggering download. */
  const handleConvertAndDownload = () => {
    try {
      if (!isXLSXLoaded || typeof XLSX === 'undefined') {
        setError("XLSX library is not available. Please wait or refresh the page.");
        return;
      }

      const data = JSON.parse(jsonInput);
      if (!Array.isArray(data) && typeof data !== 'object') {
        throw new Error("JSON data must be an array of objects or a single object.");
      }

      const arrayData = Array.isArray(data) ? data : [data];

      if (arrayData.length === 0) {
        setError("JSON data is empty.");
        return;
      }

      const ws = XLSX.utils.json_to_sheet(arrayData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `converted_data_${Date.now()}.xlsx`);
      setError('');
    } catch (err) {
      console.error("Error converting JSON to Excel:", err);
      setError(`${t('invalidJsonInput')} ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white flex items-center gap-2">
        <FileText className="w-6 h-6" /> {t('jsonToExcel')}
      </h2>

      <div>
        <label htmlFor="jsonInput" className="block text-sm font-medium text-gray-300 mb-2">
          {t('enterJsonData')}
        </label>
        <textarea
          id="jsonInput"
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          placeholder='[{"col1": "data1", "col2": "data2"}, {"col1": "data3", "col2": "data4"}]'
          rows={15}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 resize-y font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
          disabled={!isXLSXLoaded}
        ></textarea>
        {error && <p className="text-red-400 mt-2">{error}</p>}
        {!isXLSXLoaded && <p className="text-yellow-400 mt-2">Loading Excel library...</p>}
      </div>

      <Button
        onClick={handleConvertAndDownload}
        disabled={!jsonInput.trim() || !isXLSXLoaded}
        className="w-full flex items-center justify-center gap-2"
      >
        <Download className="w-4 h-4" />
        {t('convertAndDownload')}
      </Button>
    </div>
  );
};


/**
 * TextToCsvConverter component for converting plain text key-value pairs to CSV.
 * Requires XLSX library for CSV generation utility.
 * @param {object} props - Component props.
 * @param {object} props.t - Translation function.
 * @param {boolean} props.isXLSXLoaded - Indicates if the XLSX library is loaded.
 */
const TextToCsvConverter = ({ t, isXLSXLoaded }) => {
  const [textInput, setTextInput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState('');

  /**
   * Converts the input text (key:value pairs) into CSV format.
   */
  const handleConvert = () => {
    setError('');
    setCsvOutput('');

    if (!textInput.trim()) {
      setError("Please enter text to convert.");
      return;
    }

    if (!isXLSXLoaded || typeof XLSX === 'undefined') {
      setError("XLSX library is not available. Please wait or refresh the page.");
      return;
    }

    const lines = textInput.split('\n').map(line => line.trim()).filter(line => line);
    const dataForCsv = [];

    dataForCsv.push(['Key', 'Value']);

    for (const line of lines) {
      const match = line.match(/^\s*(["']?)(.*?)\1\s*[:,\s]+\s*(["']?)(.*)\3\s*,?\s*$/);

      if (match) {
        const key = match[2].trim();
        const value = match[4].trim();
        dataForCsv.push([key, value]);
      } else {
        setError(`Could not parse line: "${line}". Expected "key": "value" or key:value format.`);
        setCsvOutput('');
        return;
      }
    }

    if (dataForCsv.length <= 1) { // Only headers or no data
      setError("No valid key-value pairs found in input.");
      setCsvOutput('');
      return;
    }

    try {
      const ws = XLSX.utils.aoa_to_sheet(dataForCsv); // Array of arrays to sheet
      const csv = XLSX.utils.sheet_to_csv(ws);
      setCsvOutput(csv);
    } catch (err) {
      console.error("Error generating CSV:", err);
      setError(`Error generating CSV: ${err.message}`);
      setCsvOutput('');
    }
  };

  /** Handles copying the CSV output to the clipboard. */
  const handleCopy = async () => {
    if (!csvOutput) return;
    try {
      await navigator.clipboard.writeText(csvOutput);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = csvOutput;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  /** Handles downloading the CSV output as a file. */
  const downloadCsv = () => {
    if (!csvOutput) return;
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `converted_text_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white flex items-center gap-2">
        <BookText className="w-6 h-6" /> {t('textToCsv')}
      </h2>

      <div>
        <label htmlFor="textInput" className="block text-sm font-medium text-gray-300 mb-2">
          {t('enterKeyValueText')}
        </label>
        <textarea
          id="textInput"
          value={textInput}
          onChange={(e) => {
            setTextInput(e.target.value);
            setError(''); // Clear error on input change
            setCsvOutput(''); // Clear output on input change
          }}
          placeholder={`"my bets": "My Bets"\nstatement: Statement\n"hi,": "Hi,"`}
          rows={10}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 resize-y font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
          disabled={!isXLSXLoaded}
        ></textarea>
        {error && <p className="text-red-400 mt-2">{error}</p>}
        {!isXLSXLoaded && <p className="text-yellow-400 mt-2">Loading Excel library...</p>}
      </div>

      <Button
        onClick={handleConvert}
        disabled={!textInput.trim() || !isXLSXLoaded}
        className="w-full flex items-center justify-center gap-2"
      >
        <Download className="w-4 h-4" />
        {t('convertAndDownloadCsv')}
      </Button>

      {csvOutput && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-200">{t('parsedOutput')} (CSV)</h3>
          <div className="bg-gray-900 p-4 rounded-md text-gray-200 font-mono text-sm overflow-auto max-h-96 border border-gray-700">
            <pre>{csvOutput}</pre>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={downloadCsv}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {t('downloadCsv')}
            </Button>
            <Button
              onClick={handleCopy}
              className={cn("flex items-center gap-2", copySuccess ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white')}
            >
              <Copy className="w-4 h-4" />
              {copySuccess ? t('csvCopied') : t('copyOutput')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Main Enhanced App Component for the multi-language key-value translator.
 */
export default function App() {
  const [activeTab, setActiveTab] = useState('translator'); // New state for active tab
  const [keyInput, setKeyInput] = useState('');
  const [valueInput, setValueInput] = useState('');
  const [projectSelect, setProjectSelect] = useState('tycho');
  const [isLoading, setIsLoading] = useState(false);
  const [terminalLog, setTerminalLog] = useState([]);
  const [lastTranslatedJSON, setLastTranslatedJSON] = useState('');
  const [translationStats, setTranslationStats] = useState({ success: 0, errors: 0, total: 0 });
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', jsonOutput: '' });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ translationDelay: 100, compactMode: false });
  const [currentLanguage, setCurrentLanguage] = useState('en'); // For UI translation
  const [isXLSXLoaded, setIsXLSXLoaded] = useState(false); // New state to track XLSX library loading

  // Load XLSX library dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    script.async = true;
    script.onload = () => {
      console.log("XLSX library loaded successfully.");
      setIsXLSXLoaded(true);
    };
    script.onerror = (error) => {
      console.error("Failed to load XLSX library:", error);
      setIsXLSXLoaded(false);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);


  // Translation function for UI text based on the selected language
  const t = useCallback((key, ...args) => {
    const translationSet = translations[currentLanguage] || translations['en']; // Fallback to English
    const translated = translationSet[key];
    if (typeof translated === 'function') {
      return translated(...args);
    }
    return translated || translations['en'][key] || key; // Fallback to English key or original key
  }, [currentLanguage]);

  // Function to add a log message to the terminal output
  const addLog = useCallback((text, type = 'info') => {
    setTerminalLog(prevLogs => {
      const newLog = { id: Date.now() + Math.random(), text, type }; // Unique ID for React key
      return [...prevLogs, newLog];
    });
  }, []);

  // Clear console logs and show initial messages
  const handleClearConsole = useCallback(() => {
    setTerminalLog([]);
    addLog(t('translatorReady'), 'info');
    addLog(t('newFeatures'), 'info');
    addLog(t('customizeSettings'), 'info');
    addLog(t('readyForTasks'), 'prompt');
  }, [addLog, t]);

  // Effect to run initial console messages on component mount
  useEffect(() => {
    handleClearConsole();
  }, [handleClearConsole]);

  // Callback for updating settings
  const handleSettingsChange = useCallback((newSettings) => {
    setSettings(newSettings);
    addLog(`Settings updated: Delay ${newSettings.translationDelay}ms, Compact Mode: ${newSettings.compactMode ? 'On' : 'Off'}`, 'info');
  }, [addLog]);

  // Callback for opening the result modal
  const handleOpenResultModal = useCallback((json, stats) => {
    setLastTranslatedJSON(json);
    setTranslationStats(stats);
    setModalContent({
      title: t('translationComplete'),
      message: t('resultsSummary', stats.total, stats.success, stats.errors),
      jsonOutput: json
    });
    setIsModalOpen(true);
  }, [t]);

  // Callback for closing the result modal
  const handleCloseResultModal = useCallback(() => {
    setIsModalOpen(false);
    setModalContent({ title: '', message: '', jsonOutput: '' });
  }, []);

  // Callback for when JSON is copied from the result modal
  const handleCopyJson = useCallback(() => {
    if (!lastTranslatedJSON) {
      addLog(t('noJsonToCopy'), 'warning');
      return;
    }
    addLog(t('jsonCopied'), 'success');
  }, [lastTranslatedJSON, addLog, t]);

  // Handle single key-value pair translation
  const handleTranslateSingle = useCallback(async () => {
    if (!keyInput.trim() || !valueInput.trim()) {
      addLog(t('errorEnterKeyValue'), 'error');
      return;
    }

    setIsLoading(true);
    setTerminalLog([]); // Clear logs for new translation
    setCurrentProgress(0);
    setTranslationStats({ success: 0, errors: 0, total: 0 });

    addLog(t('startingBatch', 1), 'prompt'); // Consistent logging with batch
    const targetLangs = originalLanguages[projectSelect];
    addLog(t('targetLanguages', targetLangs.length, targetLangs.length), 'info');
    addLog(t('processing', keyInput, valueInput), 'prompt');

    // Changed structure: outer key is language code, inner key is original translation key
    const translatedResults = {};
    let successfulTranslations = 0;
    let failedTranslations = 0;
    let completedTranslations = 0;

    for (const lang of targetLangs) {
      const libreLang = lang; // Use the full locale code (e.g., "bn_BD")
      try {
        const translatedValue = await translateWithGoogle(valueInput, libreLang);
        if (!translatedResults[libreLang]) {
          translatedResults[libreLang] = {};
        }
        translatedResults[libreLang][keyInput] = translatedValue; // Assign to correct structure
        addLog(t('translatedValue', lang, translatedValue), 'success');
        successfulTranslations++;
      } catch (error) {
        if (!translatedResults[libreLang]) {
          translatedResults[libreLang] = {};
        }
        translatedResults[libreLang][keyInput] = `ERROR: ${error.message}`;
        addLog(t('translationFailed', lang, error.message), 'error');
        failedTranslations++;
      } finally {
        completedTranslations++;
        setCurrentProgress(completedTranslations);
        setTranslationStats({
          success: successfulTranslations,
          errors: failedTranslations,
          total: targetLangs.length
        });
        await delay(settings.translationDelay); // Delay between each language translation
      }
    }

    addLog(t('batchCompleted'), 'info');
    addLog(t('finalStats', successfulTranslations, failedTranslations), 'info');
    handleOpenResultModal(JSON.stringify(translatedResults, null, 2), {
      success: successfulTranslations,
      errors: failedTranslations,
      total: targetLangs.length
    });
    setIsLoading(false);
  }, [keyInput, valueInput, projectSelect, settings.translationDelay, addLog, handleOpenResultModal, t]);

  const [isBatchInputOpen, setIsBatchInputOpen] = useState(false);

  // Callback to open batch input modal
  const handleOpenBatchInput = useCallback(() => {
    setIsBatchInputOpen(true);
  }, []);

  // Callback to close batch input modal
  const handleCloseBatchInput = useCallback(() => {
    setIsBatchInputOpen(false);
  }, []);

  // Handle batch translation from parsed input (called by BatchInput component)
  const handleBatchInputSubmit = useCallback(async (pairs) => {
    setIsLoading(true);
    setTerminalLog([]); // Clear logs for new translation
    setCurrentProgress(0);
    setTranslationStats({ success: 0, errors: 0, total: 0 });

    addLog(t('startingBatch', pairs.length), 'prompt');
    const targetLangs = originalLanguages[projectSelect];
    const totalOperations = pairs.length * targetLangs.length;
    addLog(t('targetLanguages', targetLangs.length, totalOperations), 'info');

    // Changed structure: outer key is language code, inner key is original translation key
    const allTranslatedResults = {};
    let successfulTranslations = 0;
    let failedTranslations = 0;
    let completedOperations = 0;

    for (const pair of pairs) {
      const { key, value } = pair;
      addLog(t('processing', key, value), 'prompt');

      for (const lang of targetLangs) {
        const libreLang = localeToLibreTranslate[lang] || lang.split('_')[0];
        try {
          const translatedValue = await translateWithGoogle(value, libreLang);
          if (!allTranslatedResults[libreLang]) {
            allTranslatedResults[libreLang] = {};
          }
          allTranslatedResults[libreLang][key] = translatedValue; // Assign to correct structure
          addLog(t('translatedValue', lang, translatedValue), 'success');
          successfulTranslations++;
        } catch (error) {
          if (!allTranslatedResults[libreLang]) {
            allTranslatedResults[libreLang] = {};
          }
          allTranslatedResults[libreLang][key] = `ERROR: ${error.message}`;
          addLog(t('translationFailed', lang, error.message), 'error');
          failedTranslations++;
        } finally {
          completedOperations++;
          setCurrentProgress(completedOperations);
          setTranslationStats({
            success: successfulTranslations,
            errors: failedTranslations,
            total: totalOperations
          });
          await delay(settings.translationDelay); // Delay between each language translation
        }
      }
    }

    addLog(t('batchCompleted'), 'info');
    addLog(t('finalStats', successfulTranslations, failedTranslations), 'info');
    handleOpenResultModal(JSON.stringify(allTranslatedResults, null, 2), {
      success: successfulTranslations,
      errors: failedTranslations,
      total: totalOperations
    });
    setIsLoading(false);
  }, [projectSelect, settings.translationDelay, addLog, handleOpenResultModal, t]);


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 to-gray-800 text-gray-100 font-inter flex flex-col items-center p-4 sm:p-6 lg:p-8">
      {/* Custom CSS for font and scrollbar */}
      <style>
        {`
                @import url('https:
                body {
                    font-family: 'Inter', sans-serif;
                }
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                ::-webkit-scrollbar-track {
                    background: #2D3748;
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb {
                    background: #4A5568;
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb:hover {
                    background: #6B7280;
                }
                `}
      </style>

      <div className="w-full max-w-7xl bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 p-6 sm:p-8 md:p-10 relative">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 pb-4 border-b border-gray-700">
          <div className="text-center sm:text-left mb-4 sm:mb-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              {t('appTitlePart1')} <span className="text-blue-400">{t('appTitlePart2')}</span>
            </h1>
            <p className="text-gray-400 text-sm mt-1">{t('poweredBySubtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Language Selector for UI */}
            <div className="relative">
              <select
                value={currentLanguage}
                onChange={(e) => setCurrentLanguage(e.target.value)}
                className="appearance-none bg-gray-800 border border-gray-600 text-white py-2 px-4 pr-8 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
              >
                {Object.entries(languageNames).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
            </div>
            {/* Settings Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsSettingsOpen(true)}
              className="bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>


        {/* Conditional Rendering based on activeTab */}
        {activeTab === 'translator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input and Controls Section (Translator) */}
            <div className="space-y-6">
              <div>
                <label htmlFor="keyInput" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('translationKey')}
                </label>
                <input
                  type="text"
                  id="keyInput"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="e.g., login_button_text"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="valueInput" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('sourceValue')}
                </label>
                <textarea
                  id="valueInput"
                  value={valueInput}
                  onChange={(e) => setValueInput(e.target.value)}
                  placeholder="e.g., Log In"
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-500"
                  disabled={isLoading}
                ></textarea>
              </div>

              <div>
                <label htmlFor="projectSelect" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('targetProject')}
                </label>
                <div className="relative">
                  <select
                    id="projectSelect"
                    value={projectSelect}
                    onChange={(e) => setProjectSelect(e.target.value)}
                    className="appearance-none w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all pr-8 cursor-pointer"
                    disabled={isLoading}
                  >
                    <option value="tycho">Tycho</option>
                    <option value="kirby">Kirby</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleTranslateSingle}
                  disabled={isLoading || !keyInput.trim() || !valueInput.trim()}
                  className="w-full sm:w-1/2 flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                  {isLoading ? t('translating') : t('translateSingle')}
                </Button>
                <Button
                  onClick={handleOpenBatchInput}
                  disabled={isLoading}
                  variant="secondary"
                  className="w-full sm:w-1/2 flex items-center justify-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {t('batchTranslate')}
                </Button>
              </div>
            </div>

            {/* Console Output Section (Translator) */}
            <div className="flex flex-col">
              {isLoading && (
                <ProgressBar
                  current={currentProgress}
                  total={translationStats.total}
                  successCount={translationStats.success}
                  errorCount={translationStats.errors}
                />
              )}
              <TerminalOutput messages={terminalLog} isCompact={settings.compactMode} />
              <div className="flex gap-3 mt-4">
                <Button onClick={handleClearConsole} variant="outline" className="flex-grow flex items-center justify-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {t('clearConsole')}
                </Button>
                <Button
                  onClick={() => handleOpenResultModal(lastTranslatedJSON, translationStats)}
                  disabled={!lastTranslatedJSON}
                  variant="secondary"
                  className="flex-grow flex items-center justify-center gap-2"
                >
                  <Info className="w-4 h-4" />
                  {t('viewLastResult')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Conditional rendering for other tabs */}
        {activeTab === 'excelConverter' && <ExcelConverter t={t} isXLSXLoaded={isXLSXLoaded} />}
        {activeTab === 'jsonToExcel' && <JsonToExcelConverter t={t} isXLSXLoaded={isXLSXLoaded} />}
        {activeTab === 'textToCsv' && <TextToCsvConverter t={t} isXLSXLoaded={isXLSXLoaded} />}
      </div>

      {/* Modals */}
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        t={t}
      />

      <ResultModal
        isOpen={isModalOpen}
        onClose={handleCloseResultModal}
        title={modalContent.title}
        message={modalContent.message}
        jsonOutput={modalContent.jsonOutput}
        onCopy={handleCopyJson}
        stats={translationStats}
        t={t}
      />

      <BatchInput
        isOpen={isBatchInputOpen}
        onClose={handleCloseBatchInput}
        onSubmit={handleBatchInputSubmit}
        t={t}
      />
    </div>
  );
}
